import { useMemo } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';

import { UseBaseTable } from 'hooks/useBaseTable';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useProductivityRateStore } from 'store/productivityRate.store';

import { useMutationExtractProductivityTableCardToCsv } from 'api/mutations/extractProductivityTableCardToCsv.mutation';
import { useQueryGetRunRateAnalysisBreakdownQuery } from 'api/queries/getRunRateAnalysisBreakdown.query';

import { getInputLabel } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter.utils';
import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { rangeOptions } from 'components/common/GlobalFilterComponent/components/RangeFilter/rangeFilter';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { aggregationOptions } from 'components/widgets/RunRateChart/productivityRate.constants';

interface Props {
    componentKey: string;
    widgetId?: string;
    contractId?: number | null | undefined;
    latestVersionId?: number | undefined;
    compareVersionId?: number | undefined;
    localFilters: any;
    globalFilterData: any;
    table: UseBaseTable['table'];
}
export type UseProductivityTableProvider = ReturnType<typeof useProductivityTableProvider>;

export const useProductivityTableProvider = ({
    localFilters,
    globalFilterData,
    widgetId,
    latestVersionId,
    compareVersionId,
    table,
    componentKey,
}: Props) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();

    const selectedStartDate = useProductivityRateStore((state) => state.selectedStartDate);
    const selectedEndDate = useProductivityRateStore((state) => state.selectedEndDate);
    const selectedValue = useProductivityRateStore((state) => state.selectedValue);
    const selectedSeries = useProductivityRateStore((state) => state.selectedSeries);
    const aggregation = useProductivityRateStore((state) => state.aggregation);

    const idList = useMemo(() => (latestVersionId ? [latestVersionId] : []), [latestVersionId]);

    const dateRangeFilterValue = useMemo(() => {
        return globalFilterData?.dateRangeFilter;
    }, [globalFilterData?.dateRangeFilter]);

    const range = useMemo(() => {
        const value = widgetId ? localFilters?.range : dateRangeFilterValue;
        return Array.isArray(value) ? 'R_CUSTOM' : value;
    }, [widgetId, localFilters?.range, dateRangeFilterValue]);

    const source = widgetId
        ? { ...localFilters, range }
        : {
              ...globalFilterData,
              startDate: selectedStartDate,
              endDate: selectedEndDate,
              range,
              actualMetaDataId: latestVersionId,
              comparedMetaDataId: compareVersionId,
              aggregation: aggregation,
              selectedSeries,
              selectedValue,
          };

    const rangeFilterValue = useMemo(() => {
        if (!source?.range) return [];
        if (source.range === 'R_CUSTOM') {
            return [getInputLabel({ startDate: source?.startDate, endDate: source?.endDate })];
        }
        return [rangeOptions.find((item) => item.value === source?.range)?.label || ''];
    }, [source?.range, source?.startDate, source?.endDate]);

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.TAG_CARD_BOARD]: source?.tags
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.WBS_SEARCH]: source?.wbs,
            [FilterNames.ACTIVITY_TYPE_FILTER]: source?.activityType,
            [FilterNames.PRODUCTIVITY_PRE_DEFINED]: source?.productivityPreDefined,
        });
    }, [
        source?.activityCodes,
        source?.milestoneFragnet,
        source?.tags,
        source?.wbs,
        source?.activityType,
        source?.productivityPreDefined,
    ]);

    const runRateAnalysisBreakdownPayload = {
        actualMetaDataId: source?.actualMetaDataId,
        comparedMetaDataId: source?.comparedMetaDataId,
        startDate: source?.startDate,
        endDate: source?.endDate,
        range: source?.range,
        dataType: source?.selectedValue,
        filter: {
            filters,
        },
        id: idList,
        sortingList: table.sorting,
        paging: { ...table.pagination, page: table.pagination.page - 1 },
        aggregation: source?.aggregation,
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    };
    const extractProductivityTableCardToCsvPayload = {
        actualMetaDataId: source?.actualMetaDataId,
        comparedMetaDataId: source?.comparedMetaDataId,

        startDate: source?.startDate,
        endDate: source?.endDate,
        range: source?.range,
        dataType: source?.selectedValue,
        filter: {
            filters,
        },
        id: idList,
        sortingList: table.sorting,
        paging: { ...table.pagination, page: table.pagination.page - 1 },
        aggregation: source?.aggregation,
    };

    return {
        ui: {
            state: getState({ source, table }),
            pills: getPills(source, widgetId, latestVersionId, rangeFilterValue),
            vars: {
                dateRangeFilterValue,
            },
        },
        api: {
            getRunRateAnalysisBreakdown: useQueryGetRunRateAnalysisBreakdownQuery(runRateAnalysisBreakdownPayload),
            extractProductivityTableCardToCsv: {
                instance: useMutationExtractProductivityTableCardToCsv(),
                payload: extractProductivityTableCardToCsvPayload,
            },
        },
    };
};

const getState = ({ source, table }) => {
    return {
        // globalFilter
        activityCodes: source?.activityCodes,
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags,
        wbs: source?.wbs,
        activityType: source?.activityType,
        productivityPreDefined: source?.productivityPreDefined,

        // local
        aggregation: source?.aggregation,
        range: source?.range,
        actualMetaDataId: source?.actualMetaDataId,
        comparedMetaDataId: source?.comparedMetaDataId,
        startDate: source?.startDate,
        endDate: source?.endDate,

        selectedSeries: source?.selectedSeries,
        selectedValue: source?.selectedValue,

        // table
        sorting: table.sorting,
    };
};

const getPills = (source, widgetId, latestVersionId, rangeFilterValue) => {
    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.value.isExclude).map((i) => i.value.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value.isExclude).map((i) => i.value.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        wbs: source?.wbs,

        activityType: source?.activityType,
        dateRangeFilter: rangeFilterValue,
        aggregation: aggregationOptions.find((item) => item.value === source?.aggregation)?.label,
        productivityPreDefined: source?.productivityPreDefined,
    };

    const { pillsList: pills } = useGlobalFiltersHook({
        widgetId,
        latestVersionId,
        source: pillsData,
    });

    return pills;
};
